<template>
  <div style="max-width: 450px; margin: 0 auto;">
    <h4 class="text-center">Visits by Web Browser</h4>
    <Doughnut v-if="chartData" :data="chartData" :options="chartOptions" />
    <div v-else>Loading...</div>
  </div>
</template>

<script setup>
/**
 * WebChartComponent
 *
 * This Vue component displays a Doughnut chart with visits grouped by web browser.
 * It uses vue-chartjs and chart.js to render the chart and axios to fetch data from the API.
 *
 * @module WebChartComponent
 */

import { ref, onMounted } from 'vue';
import { Doughnut } from 'vue-chartjs';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import axios from 'axios';

// Register required Chart.js components
Chart.register(ArcElement, Tooltip, Legend);

/**
 * Reactive data for the chart.
 * @type {import('vue').Ref<Object|null>}
 */
const chartData = ref(null);

/**
 * Configuration options for the Doughnut chart.
 * @type {Object}
 */
const chartOptions = {
  responsive: true,
  plugins: {
    legend: { display: true, position: 'bottom' },
    title: { display: true, text: 'Visits by Web Browser' }
  }
};

/**
 * Predefined colors for the chart sections.
 * @type {string[]}
 */
const colors = [
  '#fd7e14',
  '#ffc107',
  '#198754',
  '#20c997',
  '#0dcaf0'
];

onMounted(/**
 * Fetches visited links data from an API and processes it to populate a chart.
 *
 * @async
 * @function
 * @returns {Promise<void>} This function does not return any value.
 * @throws Will handle any errors encountered during the API request and fallback to a default chart structure.
 *
 * @description
 * - Retrieves the API base URL from environment variables and the user token from session storage.
 * - Makes an authenticated GET request to the `/linksvisited` endpoint to fetch visited links data.
 * - Processes the received data to count occurrences of each browser used and maps this data for charting.
 * - Updates the `chartData` object with the processed data, including labels, visit counts, and dataset styling.
 * - On failure or invalid data, sets the `chartData` object to an empty default state with no labels or data.
 */
async () => {
  const apiUrl = process.env.VUE_APP_API_URL;
  const token = sessionStorage.getItem('token');

  try {
    const res = await axios.get(`${apiUrl}/linksvisited`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    if (res && Array.isArray(res.data.linksVisited)) {
      /** @type {Object.<string, number>} */
      const counts = {};
      res.data.linksVisited.forEach(item => {
        const browser = item?.web_navigator || 'Unknown';
        counts[browser] = (counts[browser] || 0) + 1;
      });
      const labels = Object.keys(counts);
      chartData.value = {
        labels,
        datasets: [{
          label: 'Visits',
          backgroundColor: labels.map((_, i) => colors[i % colors.length]),
          data: Object.values(counts)
        }]
      };
    } else {
      chartData.value = {
        labels: [],
        datasets: [{ label: 'Visits', data: [] }]
      };
    }
  } catch (e) {
    chartData.value = {
      labels: [],
      datasets: [{ label: 'Visits', data: [] }]
    };
  }
});
</script>
